import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Game } from 'features/mines/game'
import { Container } from 'ui/container'
import { Bet } from 'features/mines/bet'
import { useState, useCallback, useEffect } from 'react'
import { CSSTransition } from 'react-transition-group'
import { Panel } from 'features/mines/panel'
import { useAuthContext } from '../../core/providers/AuthProvider'
import { minesService } from '../../core/services'
import { toast } from 'react-toastify'

import c from './assets/sass/page.module.scss'
import { SupportIcon } from '../../ui/icons'

function initCells() {
    return Array.from({ length: 25 }, (_, i) => ({
        id: i,
        type: null,
        revealed: false,
    }))
}

const MinesPage = () => {
    const { t } = useTranslation()
    const { updateUserData } = useAuthContext()
    const [hasBet, setHasBet] = useState(false)
    const [coinsFound, setCoinsFound] = useState(0)
    const [gameWon, setGameWon] = useState(false)
    const [gameLost, setGameLost] = useState(false)
    const [gameId, setGameId] = useState(null)
    const [cells, setCells] = useState(initCells)
    const [multiplier, setMultiplier] = useState(1.0)
    const [payout, setPayout] = useState('0')
    const [isRevealing, setIsRevealing] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        const restoreGame = async () => {
            try {
                const response = await minesService.getCurrentGame()
                if (cancelled) return
                if (response.game) {
                    const game = response.game
                    setGameId(game.id)
                    setMultiplier(game.multiplier || 1.0)
                    setPayout(game.payout || '0')

                    const revealed = game.revealedCells || []
                    setCoinsFound(revealed.length)
                    setCells(
                        initCells().map((cell) =>
                            revealed.includes(cell.id)
                                ? { ...cell, type: 'coin', revealed: true }
                                : cell
                        )
                    )
                    setHasBet(true)
                }
            } catch (err) {
                console.error('Ошибка загрузки активной игры:', err)
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }
        restoreGame()
        return () => {
            cancelled = true
        }
    }, [])

    const betHandler = async (bet) => {
        try {
            const response = await minesService.createGame(bet, 5)
            await updateUserData()

            setGameId(response.game.id)
            setCells(initCells())
            setCoinsFound(0)
            setMultiplier(1.0)
            setPayout('0')
            setGameWon(false)
            setGameLost(false)
            setIsRevealing(false)
            setHasBet(true)
        } catch (error) {
            console.error('Ошибка при создании игры:', error)
            const code = error.response?.data?.code
            let errorMsg
            if (code === 'PROFIT_LIMIT_REACHED') {
                errorMsg = t('mines.error.dailyLimit')
            } else {
                errorMsg = error.response?.data?.error || t('mines.error.insufficient')
            }
            toast(errorMsg, { type: 'error' })
        }
    }

    const onCellClick = useCallback(
        async (cellIndex) => {
            if (!gameId || isRevealing || gameLost || gameWon) return

            setIsRevealing(true)
            try {
                const response = await minesService.revealCell(
                    gameId,
                    cellIndex
                )
                const { game: gameData, result } = response

                if (gameData.isMine) {
                    setCells((prev) =>
                        prev.map((cell) => {
                            if (cell.id === cellIndex)
                                return {
                                    ...cell,
                                    type: 'bomb',
                                    revealed: true,
                                }
                            if (
                                gameData.minePositions &&
                                gameData.minePositions.includes(cell.id)
                            )
                                return {
                                    ...cell,
                                    type: 'bomb',
                                    revealed: true,
                                }
                            return cell
                        })
                    )
                    setGameLost(true)
                } else {
                    setCells((prev) =>
                        prev.map((cell) =>
                            cell.id === cellIndex
                                ? { ...cell, type: 'coin', revealed: true }
                                : cell
                        )
                    )
                    setCoinsFound((prev) => prev + 1)
                    setMultiplier(gameData.multiplier)
                    setPayout(gameData.payout)

                    if (result === 'WIN') {
                        setGameWon(true)
                        setHasBet(false)
                        await updateUserData()
                    }
                }
            } catch (error) {
                console.error('Ошибка при открытии клетки:', error)
                const errorMsg =
                    error.response?.data?.error || error.message
                toast(errorMsg, { type: 'error' })
            } finally {
                setIsRevealing(false)
            }
        },
        [gameId, isRevealing, gameLost, gameWon, updateUserData]
    )

    const cashOut = async () => {
        if (!gameId) return
        try {
            const response = await minesService.cashout(gameId)
            await updateUserData()

            setPayout(response.payout)
            setGameWon(true)
            setHasBet(false)

            toast(t('mines.error.success'), { type: 'success' })
        } catch (error) {
            console.error('Ошибка при кэшауте:', error)
            const errorMsg =
                error.response?.data?.error ||
                error.message ||
                t('errors.minesPayoutFallback')
            toast(errorMsg, { type: 'error' })
        }
    }

    const playAgain = () => {
        setHasBet(false)
        setCoinsFound(0)
        setGameWon(false)
        setGameLost(false)
        setGameId(null)
        setCells(initCells())
        setMultiplier(1.0)
        setPayout('0')
        setIsRevealing(false)
    }

    const navigate = useNavigate()
    const handleFAQ = () => {
        navigate('/faq?category=modes')
    }

    return (
        <div className={c.component}>
            <div className={c.title}>
                <span className={c.mainTitle}>{t('mines.title')}</span>
                <span className={c.subTitle}>{t('mines.subtitle')}</span>
                <button type="button" className={c.btn} onClick={handleFAQ}>
                    <SupportIcon />
                </button>
            </div>
            <Container>
                <Game
                    hasBet={hasBet}
                    onPlayAgain={playAgain}
                    gameWon={gameWon}
                    gameLost={gameLost}
                    resetKey={`${hasBet}-${gameWon}`}
                    cells={cells}
                    onCellClick={onCellClick}
                    isRevealing={isRevealing}
                />
                <CSSTransition
                    in={!isLoading && !hasBet && !gameWon}
                    timeout={300}
                    classNames={{
                        enter: c.enter,
                        enterActive: c.enterActive,
                        exit: c.exit,
                        exitActive: c.exitActive,
                    }}
                    unmountOnExit
                >
                    <Bet betHandler={betHandler} />
                </CSSTransition>
                <CSSTransition
                    in={(hasBet && (coinsFound > 0 || gameLost)) || gameWon}
                    timeout={300}
                    classNames={{
                        enter: c.enter,
                        enterActive: c.enterActive,
                        exit: c.exit,
                        exitActive: c.exitActive,
                    }}
                    unmountOnExit
                >
                    <Panel
                        multiplier={multiplier}
                        payout={payout}
                        onCashOut={cashOut}
                        lost={gameLost}
                        won={gameWon}
                        onPlayAgain={playAgain}
                    />
                </CSSTransition>
            </Container>
        </div>
    )
}

export default MinesPage
